import { fabric } from 'fabric';
import { useCallback } from 'react';
import Vec2 from 'vec2';

import {
  CANVAS_CURSOR,
  CANVAS_OBJECT_TYPE_PREFIX,
  CANVAS_OBJECT_TYPE_SUFFIX,
} from '~/core/constants';
import { drawingTool } from '~/core/engine/DrawingTool';
import { DRAGGING_STATE } from '~/core/enums';
import { DrawingTabEnum } from '~/core/enums/drawing-tab.enum';
import {
  getOutlinePointsFromShape,
  isPointInsidePolygon,
} from '~/core/helpers';
import {
  CornerInfo,
  CornerRadiusModel,
  DiagonalCornerModel,
  DrawingShape,
} from '~/core/models';
import { CustomSideModel } from '~/core/models/predefined-custom-side.model';
import { hideAllLabels } from '~/core/utils';
import { findImageByName } from '~/core/utils/drawingTool/drawingTool.utils';
import {
  useDrawingShapeContext,
  useFabricCanvasContext,
} from '~/legacy/easy-estimator-page/context';
import { useCanvasSelectionHandler } from '~/legacy/easy-estimator-page/context/fabricCanvasContext/hooks';
import { useAppDispatch } from '~/redux/hooks';
import { updateNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { updateSelectedDrawingTab } from '~/redux/slices/shape';

export const useMouseDown = (canvas: fabric.Canvas) => {
  const dispatch = useAppDispatch();

  const {
    getters: { activeDrawingApplianceId },
    handlers: {
      updateActiveDrawingAppliance,
      updateActiveDrawingShapeId,
      updateDrawingShapes,
    },
  } = useDrawingShapeContext();

  const {
    draggingStateRef,
    drawingShapesRef,
    isShapeLogicRef,
    movingItemRef,
    lastPositionRef,
    shapeStartDragRef,
  } = useFabricCanvasContext();
  const { handleCornerSelection, handleEdgeSelection } =
    useCanvasSelectionHandler();

  const handleApplianceSelection = useCallback(
    (targetName: string, pointer: Vec2) => {
      // Find the shape that contains this appliance
      const shape = drawingShapesRef.current.find((dShape) =>
        dShape.appliances.some(
          (appliance) =>
            `${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}_${appliance.id}` ===
            targetName
        )
      );

      if (shape) {
        // Find the appliance object on canvas or in groups
        const applianceObject = findImageByName(
          canvas.getObjects(),
          targetName
        );

        let applianceInGroup: fabric.Image | undefined;

        if (!applianceObject && shape.fabricGroup) {
          applianceInGroup = findImageByName(
            shape.fabricGroup.getObjects(),
            targetName
          );
        }

        const targetAppliance = applianceObject || applianceInGroup;

        // If appliance is already on canvas and selected, do nothing
        if (applianceObject && activeDrawingApplianceId === targetName) {
          canvas.moveCursor = CANVAS_CURSOR.FREE_DRAWING.POINTER;

          return;
        }

        // Move previously selected appliance back to its group (if exists)
        if (
          activeDrawingApplianceId &&
          activeDrawingApplianceId !== targetName
        ) {
          const previousApplianceObject = canvas
            .getObjects()
            .find(
              (obj) =>
                obj.name === activeDrawingApplianceId && obj.type === 'image'
            ) as fabric.Image | undefined;

          if (previousApplianceObject) {
            // Find which shape contains the previous appliance
            const previousShape = drawingShapesRef.current.find((dShape) =>
              dShape.appliances.some(
                (appliance) =>
                  `${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}_${appliance.id}` ===
                  activeDrawingApplianceId
              )
            );

            if (previousShape && previousShape.fabricGroup) {
              canvas.remove(previousApplianceObject);
              previousShape.fabricGroup.addWithUpdate(previousApplianceObject);
            }
          }
        }

        // Move newly selected appliance to canvas
        if (targetAppliance) {
          if (applianceInGroup && shape.fabricGroup) {
            shape.fabricGroup.remove(targetAppliance);
          }
          canvas.add(targetAppliance);
          canvas.setActiveObject(targetAppliance);
        }
      }

      updateActiveDrawingAppliance(targetName);
      movingItemRef.current = targetName;
      shapeStartDragRef.current = pointer;
      // Don't trigger full redraw - we've moved appliances directly
      canvas.renderAll();
      canvas.moveCursor = CANVAS_CURSOR.FREE_DRAWING.POINTER;
    },
    [
      updateActiveDrawingAppliance,
      movingItemRef,
      shapeStartDragRef,
      canvas,
      drawingShapesRef,
      activeDrawingApplianceId,
    ]
  );

  const handleCircleClick = useCallback(
    (target: fabric.Object) => {
      canvas.remove(target);

      const data = target.data;

      if (data.cornerId) {
        const corner: CornerInfo =
          data.parentDrawingShape.cornerMap[data.cornerId];
        const selectedOption: CornerRadiusModel | DiagonalCornerModel =
          data.selectedOption;

        handleCornerSelection(corner, selectedOption);
      } else {
        const selectedOption: CustomSideModel = data.selectedOption;
        const shape: DrawingShape = data.parentDrawingShape;
        const edge = shape.drawingEdges.find(({ id }) => id === data.edgeId);

        if (edge) {
          handleEdgeSelection(edge, shape, selectedOption);
        }
      }

      updateDrawingShapes([...drawingShapesRef.current]);
      dispatch(updateNeedRedrawCanvas(true));
    },
    [
      canvas,
      dispatch,
      drawingShapesRef,
      handleCornerSelection,
      handleEdgeSelection,
      updateDrawingShapes,
    ]
  );

  const removeShapeObjects = useCallback(
    (shape: DrawingShape): void => {
      const withLabel = (prefix: string) => `${prefix}_${shape.label}`;

      (
        [
          [withLabel(CANVAS_OBJECT_TYPE_PREFIX.SHAPE_LABEL), true],
          [withLabel(CANVAS_OBJECT_TYPE_PREFIX.EDGE_NAME), true],
          [CANVAS_OBJECT_TYPE_PREFIX.INTERIOR_ARROW, false],
          [CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE, false],
        ] as [string, boolean][]
      ).forEach(([name, isStrict]) =>
        drawingTool.removeObjectByName(canvas, name, isStrict)
      );

      hideAllLabels(shape.editableLabels);
    },
    [canvas]
  );

  const updateShapeState = useCallback(
    (shape: DrawingShape): void => {
      // beginning of moving a shape
      movingItemRef.current = shape;

      drawingTool.removeObjectByName(
        canvas,
        CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE
      );
      updateActiveDrawingShapeId(shape.id);
      dispatch(updateNeedRedrawCanvas(true));
      dispatch(updateSelectedDrawingTab(DrawingTabEnum.SELECTED_SHAPE_TAB));
      canvas.moveCursor = CANVAS_CURSOR.DEFAULT.GRABBING;
    },
    [canvas, dispatch, movingItemRef, updateActiveDrawingShapeId]
  );

  const handleShapeInteraction = useCallback(
    (targetName: string, pointer: Vec2) => {
      const shape = drawingShapesRef.current.find(
        (dShape) =>
          targetName ===
          dShape.canvasObjectName + CANVAS_OBJECT_TYPE_SUFFIX.SHAPE_GROUP
      );

      if (!shape) {
        return;
      }

      const shapeOutlinePoints = getOutlinePointsFromShape(shape);

      shapeStartDragRef.current = pointer;
      removeShapeObjects(shape);

      hideAllLabels(shape.editableLabels);

      if (isPointInsidePolygon(shapeOutlinePoints, pointer)) {
        movingItemRef.current = shape;
        updateShapeState(shape);
      } else if (canvas.freeDrawingCursor === CANVAS_CURSOR.FREE_DRAWING.DRAW) {
        // if we are free drawing, do this (better way to test this?)
        dispatch(updateNeedRedrawCanvas(true));
        movingItemRef.current = undefined;
      } else if (isShapeLogicRef.current) {
        movingItemRef.current = shape;
        drawingTool.removeObjectByName(
          canvas,
          CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE
        );
      }
    },
    [
      drawingShapesRef,
      shapeStartDragRef,
      removeShapeObjects,
      canvas,
      isShapeLogicRef,
      movingItemRef,
      updateShapeState,
      dispatch,
    ]
  );

  const startDragging = useCallback(
    (e: MouseEvent) => {
      // If there's an active appliance, move it back to its group before deselecting
      if (activeDrawingApplianceId) {
        const applianceObject = canvas
          .getObjects()
          .find(
            (obj) =>
              obj.name === activeDrawingApplianceId && obj.type === 'image'
          ) as fabric.Image | undefined;

        if (applianceObject) {
          // Find which shape contains this appliance
          const shape = drawingShapesRef.current.find((dShape) =>
            dShape.appliances.some(
              (appliance) =>
                `${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}_${appliance.id}` ===
                activeDrawingApplianceId
            )
          );

          if (shape && shape.fabricGroup) {
            canvas.remove(applianceObject);
            shape.fabricGroup.addWithUpdate(applianceObject);
            canvas.renderAll();
          }
        }

        // Clear the active appliance
        updateActiveDrawingAppliance(undefined);
      }

      canvas.discardActiveObject();
      draggingStateRef.current = DRAGGING_STATE.DRAGGING;
      lastPositionRef.current = { x: e.clientX, y: e.clientY };
    },
    [
      canvas,
      draggingStateRef,
      lastPositionRef,
      activeDrawingApplianceId,
      drawingShapesRef,
      updateActiveDrawingAppliance,
    ]
  );

  return {
    canvas,
    handleApplianceSelection,
    handleCircleClick,
    handleShapeInteraction,
    startDragging,
  };
};
